import { useEffect, useMemo, useRef, useState } from 'react';

type ObserverRect = Omit<DOMRectReadOnly, 'toJSON'>;

const defaultState: ObserverRect = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
};

/**
 * Hook that observes element size changes using ResizeObserver.
 *
 * @param options - ResizeObserver options
 * @returns A tuple of [ref, rect] where:
 *   - ref: callback ref to attach to the element you want to observe
 *   - rect: Current dimensions and position (starts at 0 for all values)
 *
 * @example
 * ```tsx
 * const [ref, rect] = useResizeObserver();
 * // rect.height will be 0 initially, so provide a fallback:
 * const height = rect.height || calculateFallbackHeight();
 *
 * return <div ref={ref}>...</div>;
 * ```
 *
 * A callback ref rather than a `RefObject`, because the observed element
 * commonly mounts on a later render than the hook - behind a loading state, say.
 * Assigning `ref.current` re-runs no effect, so an object ref leaves the
 * observer unattached and the rect stuck at 0 for the life of the element.
 *
 * Note: During SSR or initial render, all rect values will be 0.
 * The actual dimensions are only available after the element is mounted
 * and ResizeObserver fires its first callback.
 */
export function useResizeObserver<T extends HTMLElement = any>(
  options?: ResizeObserverOptions
) {
  const frameID = useRef(0);
  const [element, setElement] = useState<T | null>(null);

  // Note: rect starts with default values (all 0s) until ResizeObserver fires.
  // This means height/width will be 0 on initial render and during SSR.
  // Consumers should provide a fallback value for the initial render:
  // const height = rect.height || fallbackHeight
  const [rect, setRect] = useState<ObserverRect>(defaultState);

  const observer = useMemo(
    () =>
      // ResizeObserver is only created in browser environments
      // In SSR, this returns null and rect remains at default values
      typeof window !== 'undefined'
        ? new ResizeObserver((entries) => {
            const entry = entries[0];

            if (entry) {
              cancelAnimationFrame(frameID.current);

              frameID.current = requestAnimationFrame(() => {
                const boxSize =
                  entry.borderBoxSize?.[0] || entry.contentBoxSize?.[0];
                if (boxSize) {
                  const width = boxSize.inlineSize;
                  const height = boxSize.blockSize;

                  setRect({
                    width,
                    height,
                    x: entry.contentRect.x,
                    y: entry.contentRect.y,
                    top: entry.contentRect.top,
                    left: entry.contentRect.left,
                    bottom: entry.contentRect.bottom,
                    right: entry.contentRect.right,
                  });
                } else {
                  setRect(entry.contentRect);
                }
              });
            }
          })
        : null,
    []
  );

  useEffect(() => {
    if (!element) return;

    observer?.observe(element, options);

    return () => {
      observer?.disconnect();

      if (frameID.current) {
        cancelAnimationFrame(frameID.current);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element]);

  return [setElement, rect] as const;
}

export function useElementSize<T extends HTMLElement = any>(
  options?: ResizeObserverOptions
) {
  const [ref, { width, height }] = useResizeObserver<T>(options);
  return { ref, width, height };
}
